import type {
  SendAnalyticsEventsFunction,
  ReplicantAnalyticsEvent,
} from '@play-co/analytics-schema';
import type { GameAnalyticsSchema } from '../../analytics.config';

export const Feature = {
  INVITE: {
    _: 'invite',
    INVITE_ICON: 'invite_icon',
    INVITE_GIFTS: 'invite_icon_energy_gift',
    INVITE_POPUP_MULTI: 'invite_popup_multi',
    INVITE_POPUP_MULTI_SELECT: 'invite_popup_multi_select',
    INVITE_POPUP_MULTI_OK: 'invite_popup_multi_ok',
    INVITE_POPUP_MULTI_CANCEL: 'invite_popup_multi_cancel',
    GEMS_ICON: 'invite_gems_icon',
    INVITE_LAUNCH: 'invite_launch',
    INVITE_UNLOCK: 'invite_unlock',
    INVITE_LOW_BALANCE: 'invite_low_balance',
    INVITE_INSTEAD_GEMS_SHOP: 'invite_instead_gems_shop',
    INVITE_NO_ENERGY: 'invite_no_energy',
    INVITE_FRIEND_JOINED_TO_GAME: 'invite_friend_joined_to_game',
    WALLPAPER_REWARD: 'invite_wallpaper_reward',
    INVITE_OK: 'invite_ok',
    INVITE_FRIENDS_TOAST: 'invite_friends_toast',
    LINE_SHARE: 'invite_line_share',
    NATIVE_SHARE: 'invite_native_share',
    /** @deprecated Old subFeature kept so in-flight legacy invite links keep resolving. New senders use `LINE_SHARE`. */
    INVITE_URL: 'invite_url',
    /** @deprecated Old subFeature kept so in-flight legacy invite links keep resolving. New senders use `NATIVE_SHARE`. */
    INVITE_ADD_FRIENDS_V2: 'invite_add_friends_v2',
    INVITE_JANKEN: 'invite_janken',
    RESULT_JANKEN: 'result_janken',
  },
  GIFTS: {
    _: 'gift',
    SEND_ENERGY: 'gift_energy_single',
    SEND_ENERGY_AND_COLLECT: 'gift_energy_and_collect',
    SEND_ENERGY_ALL: 'gift_energy_all',
    SEND_BPOINTS: 'gift_bPoint_single',
    SEND_BPOINTS_AND_COLLECT: 'gift_bPoint_and_collect',
    SEND_BPOINTS_ALL: 'gift_bPoint_all',
    SEND_BPOINTS_OA: 'gift_bPoint_oa',
    SEND_GEMS: 'gift_gems_single',
    SEND_GEMS_AND_COLLECT: 'gift_gems_and_collect',
    SEND_GEMS_ALL: 'gift_gems_all',
    GET_INACTIVE_FRIENDS: 'gift_get_inactive_friends',
    SEND_GIFTS_OA_MESSAGE: 'gift_send_gifts_oa_message',
  },
  VISIT: {
    _: 'visit',
    FRIEND: 'visit_friend',
    REWARD: 'visit_reward',
    NO_FRIENDS: 'visit_no_friends',
    NO_FRIENDS_CLOSE: 'visit_no_friends_close',
    NO_FRIENDS_INVITE: 'visit_no_friends_invite',
    REVIVE: 'visit_revive',
    REVIVE_OK: 'visit_revive_ok',
    REVIVE_CONFIRM: 'visit_revive_confirm',
    NON_FRIEND: 'visit_non_friend',
    POPUP_FRIEND_BUSY: 'visit_popup_friend_busy',
    POPUP_PLAYER_DEAD: 'visit_popup_player_dead',
    POPUP_PLAYER_IN_DAYCARE: 'visit_popup_player_dead',
    POPUP_ALREADY_VISITING: 'visit_popup_already_visiting',
    POPUP_FRIEND_IN_DAYCARE: 'visit_popup_friend_dead',
    PET_AND_DECO_ASSETS: 'visit_pet_and_deco_assets',
    END_VISIT: 'visit_end_visit',
    INVITER_DETAILS: 'visit_inviter_details',
    DETAILS: 'visit_details',
    RECENT_VISITORS: 'visit_recent_visitors',
    FRIENDS_LIST: 'visit_friends_list',
    POPUP_CONFIRM_VISIT: {
      _: 'visit_popup_confirm_visit',
      CLOSE: 'visit_popup_confirm_visit_close',
      OK: 'visit_popup_confirm_visit_ok',
    },
    POPUP_VISIT_FRIEND: {
      _: 'visit_popup_visit_friend',
      TAB_FRIENDS: 'visit_popup_visit_friend_tab_friends',
      TAB_VISITORS: 'visit_popup_visit_friend_tab_visitors',
      CLOSE: 'visit_popup_visit_friend_close',
      SELECT: 'visit_popup_visit_friend_select',
      ADD_FRIEND: 'visit_popup_visit_friend_add_friend',
    },
    POPUP_VISIT_REWARD: {
      _: 'visit_popup_visit_reward',
      CLOSE: 'visit_popup_visit_reward_close',
    },
  },
  STATUS: {
    _: 'status',
  },
  NOTIFICATION: {
    _: 'notification',
    ENERGY_FULL: 'notification_energy_full',
    D7_REENGAGEMENT: 'notification_7d_reengagement',
    D14_REENGAGEMENT: 'notification_14d_reengagement',
    D28_REENGAGEMENT: 'notification_28d_reengagement',
    RETENTION_D3: 'notification_retention_d3',
    RETENTION_D4: 'notification_retention_d4',
  },
  GREETING: {
    _: 'greeting',
    SPAWN_PET: 'greeting_spawn_pet',
    BOT_INVITE: 'greeting_bot_invite',
    HAPPY_PETS: 'greeting_happy_pets',
  },
  // A/B 0474: crate-skipper re-engagement crate-waiting messages.
  FTUE: {
    _: 'ftue',
    ONE_HOUR: 'ftue_1h',
    MORNING: 'ftue_morning',
    LUNCH: 'ftue_lunch',
    AFTERNOON: 'ftue_afternoon',
    EVENING: 'ftue_evening',
  },
  NATIVE: {
    _: 'native',
    PROMO_BUTTON: 'native-promo-button',
    POPUP_INSTALL_CLOSE: 'native-popup-install-close',
    POPUP_INSTALL_REWARD_IOS: 'native-popup-install-reward-ios',
    POPUP_INSTALL_REWARD_ANDROID: 'native-popup-install-reward-android',
    POPUP_INSTALL_REWARD_UNKNOWN: 'native-popup-install-reward-unknown',
    LINE_GUEST_LOGIN: 'native-line-guest-login',
    LINE_GUEST_LOGIN_OK: 'native-line-guest-login-ok',
    LINE_GUEST_LOGIN_CLOSE: 'native-line-guest-login-close',
    UPDATE_APP_POPUP: 'native_update_app_popup',
    UPDATE_APP_POPUP_CTA: 'native_update_app_popup_cta',
    UPDATE_APP_POPUP_SOFT: 'native_update_app_popup_soft',
    UPDATE_APP_POPUP_SOFT_CTA: 'native_update_app_popup_soft_cta',
    UPDATE_APP_POPUP_INCENTIVISED: 'native_update_app_popup_incentivised',
    UPDATE_APP_POPUP_INCENTIVISED_CTA:
      'native_update_app_popup_incentivised_cta',
    UPDATE_APP_POPUP_INCENTIVISED_REWARD:
      'native_update_app_popup_incentivised_reward',
    SET_PERSISTED_PAYLOAD: 'native_set_persisted_payload',
    REPLICANT_FLUSH: 'native_replicant_flush',
    GUEST_SELECT_SAVE: 'native_guest_select_save',
    GUEST_SELECT_SAVE_CONFIRMATION: 'native_guest_select_save_confirmation',
    GUEST_SELECT_SAVE_CONFIRM: 'native_guest_select_save_confirm',
    GUEST_SELECT_SAVE_CANCEL: 'native_guest_select_save_cancel',
    GUEST_LOGIN_REWARDS: 'native_guest_login_rewards',
    GUEST_LOGIN_REWARDS_OK: 'native_guest_login_rewards_ok',
  },

  DUO: {
    _: 'duo',
    INVITE: 'duo_invite',
    RESPONSE: 'duo_response',
    REMINDER: 'duo_reminder',
    FORM_DUO: 'duo_form_duo',
    PET_ASSETS: 'duo_pet_assets',
    MUG_ASSETS: 'duo_mug_assets',
    DETAILS: 'duo_details',
    DEBUT: 'duo_debut',
    INVITATION: 'duo_invitation',
    INVITATION_DETAILS: 'duo_invitation_details',
    SEND_RESPONSE: 'duo_send_response',
    VISIT_DETAILS: 'duo_visit_details',
    SEND_REMINDER: 'duo_send_reminder',

    REWARD_POPUP: {
      _: 'duo_reward_popup',
      CLOSE: 'duo_reward_popup_close',
    },

    POPUP_DUO_INVITATIONS: {
      _: 'duo_popup_duo_invitations',
      TAB_SENT: 'duo_popup_duo_invitations_tab_sent',
      TAB_RECEIVED: 'duo_popup_duo_invitations_tab_received',
      CLOSE: 'duo_popup_duo_invitations_close',
      SELECT: 'duo_popup_duo_invitations_select',
      REMIND: 'duo_popup_duo_invitations_remind',
    },

    RESPONSE_POPUP: {
      _: 'duo_response_popup',
      ERROR: 'duo_response_popup_error',
      CLOSE: 'duo_response_popup_close',
      CONFIRM: 'duo_response_popup_confirm',
    },

    RESPONSE_UNAVAILABLE: {
      _: 'duo_response_unavailable',
      DAYCARE: 'duo_response_unavailable_daycare',
    },

    REMINDER_CONFIRMATION_POPUP: {
      _: 'duo_reminder_confirmation_popup',
      CLOSE: 'duo_reminder_confirmation_popup_close',
      CONFIRM: 'duo_reminder_confirmation_popup_confirm',
    },

    REMINDER_SUCCESS_POPUP: {
      _: 'duo_reminder_success_popup',
      CLOSE: 'duo_reminder_success_popup_close',
    },

    RESPOSE_SUCCESS_POPUP: {
      _: 'duo_response_success_popup',
      CLOSE: 'duo_response_success_popup_close',
    },
  },

  DEBUT: {
    _: 'debut',
    INVITER: 'debut_inviter',
    INVITEE: 'debut_invitee',
  },

  FAMILY: {
    _: 'family_tree',
    GROWTH_POPUP: {
      _: 'family_tree_growth_popup',
      CLOSE: 'family_tree_growth_popup_close',
    },
    TREE_POPUP: {
      _: 'family_tree_popup',
      CLOSE: 'family_tree_popup_close',
      RECORD: 'family_tree_popup_record',
    },
  },

  DECORATION: {
    _: 'decoration',
    PURCHASE_CONFIRMATION_POPUP: 'decoration_purchase_confirmation_popup',
    PURCHASE_CONFIRMATION_POPUP_OK: 'decoration_purchase_confirmation_popup_ok',
    CANCEL: 'decoration_cancel',
    SAVE: 'decoration_save',
    CHANGE_BUTTON: 'decoration_change_button',
    ITEM_SELECTED: 'decoration_item_selected',
    SELECT_ROOM_POPUP: {
      _: 'decoration_select_room_popup',
      BACK: 'decoration_select_room_popup_back',
      SELECTED: 'decoration_select_room_popup_selected',
    },
  },

  DAYCARE: {
    _: 'daycare',
    SIX_HOUR: 'daycare_6-hour',
    HOME_BUTTON: 'daycare_home_button',
    ACTION: 'daycare_action',
    PURCHASE: 'daycare_purchase',
    PURCHASE_BACK: 'daycare_purchase_back',
    PURCHASE_OK: 'daycare_purchase_ok',
    EXIT: 'daycare_exit',
    EXIT_BACK: 'daycare_exit_back',
    EXIT_OK: 'daycare_exit_ok',
  },

  MINIGAME: {
    _: 'minigame',
    SCENE_PRELOAD: 'minigame_scene_preload',
  },

  PROMO: {
    _: 'promo',
  },

  CARE: {
    _: 'care',
    ASSETS: 'care_assets',
  },

  GACHA: {
    _: 'gacha',
    FOOTER_BUTTON: 'gacha_footer_button',
    SELECT_POPUP: {
      _: 'gacha_select_popup',
      ITEM: 'gacha_select_popup_item',
    },
    POPUP: {
      _: 'gacha_popup',
    },
    INTRO: 'gacha_intro',
    INTRO_PLAY: 'gacha_intro_play',
    PURCHASE: 'gacha_purchase',
    PURCHASE_BACK: 'gacha_purchase_back',
    PURCHASE_OK: 'gacha_purchase_ok',
    RESULTS_CONTINUE: 'gacha_results_continue',
    RESULTS_OK: 'gacha_results_ok',
    REROLL: 'gacha_reroll',
    SKIN_ASSETS: 'gacha_skin_assets',
    OPEN_INFO: 'gacha_open_info',
    INFO: 'gacha_info',
    INFO_OK: 'gacha_info_ok',

    EVENT_GACHA_SHOP_BUTTON: 'gacha_event_shop_button',
  },

  CATALOG: {
    _: 'catalog',
    SELL: 'catalog_sell',
    FUSION: 'catalog_fusion',
    POPUP_ITEM_SELL: {
      _: 'catalog_item_sell',
      OK: 'catalog_item_sell_ok',
      BACK: 'catalog_item_sell_back',
    },
    POPUP_ITEMS_LIST: {
      _: 'catalog_items_list',
      BACK: 'catalog_items_list_back',
      SORT: 'catalog_items_list_sort',
      SWITCH_ROOM: 'catalog_items_list_switch_room',
      SELL_ITEM_CLICK: 'catalog_items_list_sell_item_click',
    },
  },

  ENCYCLOPEDIA: {
    _: 'encyclopedia',
    POPUP_DETAILS: {
      _: 'encyclopedia_popup_details',
      BACK: 'encyclopedia_popup_details_back',
    },
    POPUP: {
      _: 'encyclopedia_popup',
      BACK: 'encyclopedia_popup_back',
      DETAILS: 'encyclopedia_popup_details',
    },
  },

  GROWTHCAPSULE: {
    _: 'growthcapsule',
    PURCHASE_POPUP: 'growthcapsule_purchase_popup',
    PURCHASE_BACK: 'growthcapsule_purchase_back',
    PURCHASE_OK: 'growthcapsule_purchase_ok',
  },

  DECORATION_PROGRESSION: {
    _: 'decoration_progression',
    STARS_PINCH: {
      _: 'decoration_progression_stars_pinch',
      PLAY: 'decoration_progression_stars_pinch_play',
      CLOSE: 'decoration_progression_stars_pinch_close',
    },
  },

  MATCH2: {
    _: 'match2',
    PLAY: 'match2_play',
    QUIT: {
      BUTTON: 'match2_quit_button',
      POPUP: 'match2_quit_popup',
      CONFIRM: 'match2_quit_confirm',
      CANCEL: 'match2_quit_cancel',
    },
    PREGAME_POPUP: 'match2_pregame_popup',
    PUZZLE_COMPLETE_POPUP: 'match2_puzzle_complete_popup',
    PUZZLE_FAILED_POPUP: 'match2_puzzle_failed_popup',
    BOOSTER_SELECT: 'match2_booster_select',
    PURCHASE_BOOSTER_POPUP: 'match2_purchase_booster_popup',
    PURCHASE_BOOSTER_BACK: 'match2_purchase_booster_back',
    PURCHASE_BOOSTER_OK: 'match2_purchase_booster_ok',
    POWERUP_SELECT: 'match2_powerup_select',
    PURCHASE_POWERUP_POPUP: 'match2_purchase_powerup_popup',
    PURCHASE_POWERUP_BACK: 'match2_purchase_powerup_back',
    PURCHASE_POWERUP_OK: 'match2_purchase_powerup_ok',
    PURCHASE_PINCH_POPUP: 'match2_purchase_pinch_popup',
    PURCHASE_PINCH_BACK: 'match2_purchase_pinch_back',
    PURCHASE_PINCH_OK: 'match2_purchase_pinch_ok',
    FREE_CONTINUE_POPUP: 'match2_free_continue_popup',
    FREE_CONTINUE_OK: 'match2_free_continue_ok',
    FREE_CONTINUE_BACK: 'match2_free_continue_back',
    REGION_ASSETS: 'match2_region_assets',
    LEVEL_ASSETS: 'match2_level_assets',
    WACTH_REWARDED_AD: 'match2_watch_rewarded_ad',
    EXTRA_MOVES_PURCHASE: 'match2_extra_moves_purchase',
    CHECK_SOCIAL_CONTINUE_AVAILABLE: 'match2_check_social_continue_available',
    DAILY_BONUS: {
      _: 'match2_daily_bonus',
      BUTTON: 'match2_daily_bonus_button',
      COLLECT: 'match2_daily_bonus_collect',
      TOS_NUDGE: {
        _: 'match2_daily_bonus_tos_nudge',
        CONTINUE: 'match2_daily_bonus_tos_nudge_continue',
      },
    },
    PUZZLE_PINCH_TOS_NUDGE: {
      _: 'match2_puzzle_pinch_tos_nudge',
      CONTINUE: 'match2_puzzle_pinch_tos_nudge_continue',
    },
    FRENZY_INTRO_POPUP: 'match2_frenzy_intro_popup',
    PLAY_ON_APP_POPUP: 'match2_play_on_app_popup',
    TOURNAMENT: {
      _: 'match2_tournament',
      BUTTON: 'match2_tournament_button',
      POPUP: 'match2_tournament_popup',
      COLLECT: 'match2_tournament_collect',
      REWARDS: 'match2_tournament_rewards',
    },
    DAILY_CHALLENGE: {
      _: 'match2_daily_challenge',
      BUTTON: 'match2_daily_challenge_button',
      POPUP: 'match2_daily_challenge_popup',
    },

    RACES: {
      _: 'match2_races',
      INTRO_POPUP: {
        _: 'match2_races_intro_popup',
      },
      SIDE_BUTTON: 'match2_races_side_button',
      LEADERBOARD_INFO: 'match2_races_leaderboard_info',
    },
    SHOP_BUNDLES: {
      _: 'match2_shop_bundles',
    },
    BUNDLE_SALE: {
      _: 'match2_bundle_sale',
      POPUP: 'match2_bundle_sale_popup',
      SIDE_BUTTON: 'match2_bundle_sale_side_button',
      REWARDS_POPUP: 'match2_bundle_sale_rewards_popup',
    },
    CONVERSION_SALE: {
      _: 'conversion_sale',
    },
    GRANT_REWARDS_POPUP: 'match2_grant_rewards_popup',
    LEAGUES: {
      _: 'match2_leagues',
      INTRO_POPUP: 'match2_leagues_intro_popup',
      SIDE_BUTTON: 'match2_leagues_side_button',
      LEADERBOARD: 'match2_leagues_leaderboard',
      LEADERBOARD_TAB: 'match2_leagues_leaderboard_TAB',
      REWARDS: 'match2_leagues_reward',
    },
    MAGIC_LAMPS: {
      _: 'match2_magic_lamps',
      POPUP: 'match2_magic_lamps_popup',
      SIDE_BUTTON: 'match2_magic_lamps_side_button',
      START: 'match2_magic_lamps_start',
      CONTINUE: 'match2_magic_lamps_continue',
      COLLECT: 'match2_magic_lamps_collect',
      CANCEL: 'match2_magic_lamps_cancel',
    },
    BATTLE_PASS: {
      _: 'match2_battle_pass',
      TUTORIAL_POPUP_CONTINUE: 'match2_battle_pass_tutorial_popup_continue',
    },
    INVITE_CELEBRATION: {
      _: 'match2_invite_celebration',
      NEW_PET: 'invite_new_pet',
      PET_EVOLVE: 'invite_pet_evolve',
      MATCH_LEVEL: 'invite_match_level',
    },
    TEAM: {
      _: 'match2_team',
      INTRO_POPUP: 'match2_team_intro_popup',
      TAB_TEAMS: 'match2_team_tab_teams',
      TAB_CREATE: 'match2_team_tab_create',
      TAB_SEARCH: 'match2_team_tab_search',
      TAB_CHAT: 'match2_team_tab_chat',
      TAB_MY_TEAM: 'match2_team_tab_my_team',
      TAB_CREATE_BROWSE: 'match2_team_tab_create_browse',
      TAB_CREATE_CREATE: 'match2_team_tab_create_create',
      TAB_CREATE_REQUIRED_LEVEL_INCREASE:
        'match2_team_tab_create_required_level_increase',
      TAB_CREATE_REQUIRED_LEVEL_DECREASE:
        'match2_team_tab_create_required_level_decrease',
      TAB_SEARCH_CTA: 'match2_team_search_cta',
      TAB_SEARCH_CLEAR: 'match2_team_search_clear',
      LEAVE_POPUP: {
        _: 'match2_team_leave_popup',
        LEAVE_POPUP_YES: 'match2_team_leave_yes_popup',
        LEAVE_POPUP_NO: 'match2_team_leave_no_popup',
      },
      INFO_POPUP: {
        _: 'match2_team_info_popup',
        JOIN: 'match2_team_info_popup_join',
        LEAVE: 'match2_team_info_popup_leave',
      },
      SWITCH_POPUP: {
        _: 'match2_team_switch_popup',
        YES_BUTTON: 'match2_team_switch_yes_button',
        NO_BUTTON: 'match2_team_switch_no_button',
      },
      CREATE: 'match2_team_create',
      EVENT_REWARDS: 'match2_team_event_rewards',
      JOIN: 'match2_team_join',
      INFO: 'match2_team_info',
      TAB_CHAT_AUTO_HELP_REQUEST_TOGGLE:
        'match2_team_tab_chat_auto_help_request_toggle',
      TAB_CHAT_REQUEST_ENERGY: 'match2_team_tab_chat_request_energy',
      TAB_CHAT_CHAT_BUTTON: 'match2_team_tab_chat_chat_button',
      TAB_CHAT_EMOTE_BUTTON: 'match2_team_tab_chat_emote_button',
      TAB_CHAT_SEND_HELP: 'match2_team_tab_chat_send_help',
      POPUP_EMOTES: 'match2_team_popup_emotes',
      POPUP_TEXT_MESSAGES: 'match2_team_popup_text_messages',
      POPUP_CONFIRM_VISIT: 'match2_team_popup_confirm_visit',
      COMPETITION: {
        _: 'match2_team_competition',
        TAB_COMPETITION: 'match2_team_competition_tab_competition',
        TAB_MY_TEAM: 'match2_team_competition_tab_my_team',
        INTRO_POPUP: 'match2_team_competition_intro_popup',
        INFO_POPUP: 'match2_team_competition_info_popup',
        INFO_BUTTON: 'match2_team_competition_info_button',
        INFO_POPUP_CONTINUE: 'match2_team_competition_info_popup_continue',
        SIDE_BUTTON: 'match2_team_competition_side_button',
      },
      CHEST: {
        _: 'match2_team_chest',
        INTRO_POPUP: 'match2_team_chest_intro_popup',
        LEADERBOARD_POPUP: 'match2_team_chest_leaderboard_popup',
        OPEN_BUTTON: 'match2_team_chest_open_button',
        EVENT_REWARDS: 'match2_team_chest_event_rewards',
      },
    },
    SOCIAL_CONTINUE: {
      POPUP: 'match2_social_continue_popup',
      POPUP_INVITE: 'match2_social_continue_popup_invite',
      POPUP_CLOSE: 'match2_social_continue_popup_close',
    },
    AD_CONTINUE: {
      POPUP: 'match2_ad_continue_popup',
      WATCH: 'match2_ad_continue_watch',
      CLOSE: 'match2_ad_continue_close',
    },
    AD_CONTINUE_STREAK: {
      POPUP: 'match2_ad_continue_streak_popup',
      WATCH: 'match2_ad_continue_streak_watch',
      CLOSE: 'match2_ad_continue_streak_close',
    },
    STREAK_EVENT: {
      _: 'match2_streak_event',
      INTRO_POPUP: 'match2_streak_event_intro_popup',
      MATCHMAKING_POPUP: 'match2_streak_event_matchmaking_popup',
      MATCHMAKING_POPUP_CONTINUE:
        'match2_streak_event_matchmaking_popup_continue',
      POPUP: 'match2_streak_event_popup',
      POPUP_PLAY: 'match2_streak_event_popup_play',
      POPUP_CONTINUE: 'match2_streak_event_popup_continue',
      POPUP_CLOSE: 'match2_streak_event_popup_close',
      POPUP_INFO: 'match2_streak_event_popup_info',
      REWARD_POPUP: 'match2_streak_event_reward_popup',
      RETRY_POPUP: 'match2_streak_event_retry_popup',
      SIDE_BUTTON: 'match2_streak_event_side_button',
    },
    BROKEN_PUZZLE: 'match2_broken_puzzle',
  },

  BUMBUM: {
    _: 'bumbum',
    LEADERBOARD_REWARD: 'bumbum_leaderboard_reward',
  },

  PUSH_NOTIFICATION: {
    _: 'push_notification',
    PERMISSION_REMINDER_POPUP: {
      _: 'push_notification_permission_reminder_popup',
      OK: 'push_notification_permission_reminder_popup_ok',
    },
    PERMISSION_POPUP: {
      _: 'push_notification_permission_popup',
      OK: 'push_notification_permission_popup_ok',
    },
  },

  FRENZY: {
    _: 'frenzy',
    FRENZY_MINIGAME: {
      _: 'frenzy_minigame',
    },
    LEADERBOARD_REWARD: 'frenzy_leaderboard_reward',
  },

  KOMOJU: {
    _: 'komoju',
    CREATE_PAYMENT: 'komoju_create_payment',
    REPLICANT_FLUSH: 'komoju_replicant_flush',
  },

  MARKETING: {
    _: 'marketing',
    COLLECT: 'marketing_collect',
    FETCH_EVENTS: 'marketing_fetch_events',
  },

  CONTINUE: {
    _: 'continue',
    SOCIAL: 'continue_social',
    TOS: 'continue_tos',
  },
  FRIENDS: {
    _: 'friends',
    POPUP: 'friends_popup',
    POPUP_CLOSE: 'friends_popup_close',
    POPUP_TUTORIAL: 'friends_popup_tutorial',
    POPUP_TUTORIAL_OK: 'friends_popup_tutorial_ok',
    INVITE: 'friends_invite',
    TAB_SEARCH: 'friends_tab_search',
    TAB_REQUESTS: 'friends_tab_requests',
    TAB_MY_FRIENDS: 'friends_tab_my_friends',
    SEARCH_PLAYERS: 'friends_search_players',
    ACCEPT_REQUEST: 'friends_accept_request',
    REJECT_REQUEST: 'friends_reject_request',
    ADD_FRIEND: 'friends_add_friend',
    UNFRIEND: 'friends_unfriend',
    FRIENDSHIP_REQUEST_OA: 'friends_friendship_request_oa',
    REQUEST: 'friends_request',
    ACCEPT: 'friends_accept',
    GET_VISIT_DETAILS: 'friends_get_visit_details',
    USER_PROFILE: 'friends_user_profile',
    USER_PROFILE_VISIT: 'friends_user_profile_visit',
    USER_PROFILE_ACCEPT: 'friends_user_profile_accept',
    USER_PROFILE_REJECT: 'friends_user_profile_reject',
    VIEW_FRIEND_PROFILE: 'friends_view_friend_profile',
    VIEW_REQUEST_PROFILE: 'friends_view_request_profile',
    POPUP_CONFIRM_REQUEST: {
      _: 'friends_popup_confirm_request',
      OK: 'friends_popup_confirm_request_ok',
      CLOSE: 'friends_popup_confirm_request_close',
    },
    POPUP_CONFIRM_REMOVE: {
      _: 'friends_popup_confirm_remove',
      KEEP: 'friends_popup_confirm_remove_keep',
      REMOVE: 'friends_popup_confirm_remove_remove',
    },
    LINE_SHARE: 'friends_line_share',
    NATIVE_SHARE: 'friends_native_share',
    QRCODE_SHARE: 'friends_qrcode_share',
    /** @deprecated Old subFeature kept so in-flight legacy v2 links keep resolving. New senders use `LINE_SHARE`. */
    FIND_LINESHARE: 'friends_find_lineshare',
    /** @deprecated Old subFeature kept so in-flight legacy v2 links keep resolving. New senders use `NATIVE_SHARE`. */
    FIND_URLSHARE: 'friends_find_urlshare',
    /** @deprecated Old subFeature kept so in-flight legacy v2 links keep resolving. New senders use `QRCODE_SHARE`. */
    FIND_QRCODE: 'friends_find_qrcode',
  },

  /**
   * A/B 0468 V2 — pet "want_to_add_friend" dialog direct-share funnel.
   * Lives in its own namespace so receiver-side events can be attributed
   * back to the pet nudge instead of the generic `friends` share flow.
   */
  PET_DIALOG: {
    _: 'pet_dialog',
    LINE_SHARE: 'pet_dialog_line_share',
    NATIVE_SHARE: 'pet_dialog_native_share',
    QRCODE_SHARE: 'pet_dialog_qrcode_share',
  },

  /**
   * Friend List popup direct-share funnel, resolved from A/B 0470.
   * Lives in its own namespace so receiver-side events can be attributed
   * back to the Friend List CTA instead of the generic `friends` share flow.
   */
  FRIEND_LIST: {
    _: 'friend_list',
    LINE_SHARE: 'friend_list_line_share',
    NATIVE_SHARE: 'friend_list_native_share',
    /** @deprecated Old subFeature kept so in-flight legacy Friend List links keep resolving. New senders use `LINE_SHARE`. */
    LEGACY_LINE_SHARE: 'friend_list_line',
    /** @deprecated Old subFeature kept so in-flight legacy Friend List links keep resolving. New senders use `NATIVE_SHARE`. */
    LEGACY_NATIVE_SHARE: 'friend_list_native',
  },

  RESUBSCRIBE: {
    _: 'resubscribe',
    REWARD: 'resubscribe_reward',
    INCENTIVIZED_OA_REWARD: 'resubscribe_incentivized_oa_reward',
  },

  AD: {
    _: 'ad',
    REWARDED_VIDEO: 'ad_rewarded_video',
  },

  OPENWEB: {
    _: 'openweb',
    PAYMENT: {
      _: 'openweb_payment',
    },
  },

  OPEN_WEB_DEMO: {
    _: 'open_web_demo',
    REDIRECT: 'open_web_demo_redirect',
    POPUP_LINE_LOGIN: {
      _: 'open_web_demo_popup_line_login',
      LOGIN_BUTTON: 'open_web_demo_popup_line_login_button',
    },
  },

  CARDS: {
    _: 'cards',
    COLLECTION: 'cards_collection',
    POPUP_INFO: 'cards_popup_info',
    SIDE_BUTTON: 'cards_side_button',
    START_BUTTON: 'cards_start_button',
    POPUP_PACK_REWARD: 'cards_popup_pack_reward',
    POPUP_ALBUM_REWARD: 'cards_popup_album_reward',
    BUTTON_START: 'cards_button_start',
    BUTTON_INFO: 'cards_button_info',
    REQUEST: 'cards_request',
    REQUEST_LINE_SHARE: 'cards_request_line_share',
    REQUEST_NATIVE_SHARE: 'cards_request_native_share',
    DONATE: 'cards_donate',
    TRADE: 'cards_trade',
    SEND: 'cards_send',
    CANCEL: 'cards_cancel',
    ACCEPT: 'cards_accept',
    COUNTER: 'cards_counter',
    REJECT: 'cards_reject',
    OK: 'cards_ok',
    DETAILS: 'cards_details',
    COLLECT_ALBUM_BUTTON: 'cards_collect_album_button',
    INBOX_BUTTON: 'cards_inbox_button',
    POPUP_RECEIVED: 'cards_popup_received',
    POPUP_RECEIVED_COLLECT: 'cards_popup_received_collect',
    RECEIVED: 'cards_received',
    CLAIM_COLLECTION_REWARDS_BUTTON: 'cards_claim_collection_rewards_button',
    CLAIM_ALBUM_REWARDS_BUTTON: 'cards_claim_album_rewards_button',
    GET_FRIENDS: 'cards_get_friends',
  },

  CHATBOT: {
    _: 'chatbot',
    WEBHOOK_AUTO_RESPONSE: 'chatbot_webhook_auto_response',
  },

  PET: {
    _: 'pet',
    ASSETS: 'pet_assets',
    EVOLUTION_ASSETS: 'pet_evolution_assets',
  },

  ASCENSION: {
    _: 'ascension',
    ASSETS: 'ascension_assets',
    CRATE_ASSETS: 'ascension_crate_assets',
    PET_ASSETS: 'ascension_pet_assets',
  },

  TUTORIAL: {
    _: 'tutorial',
    ASSETS: 'tutorial_assets',
    CRATE_ASSETS: 'tutorial_crate_assets',
    PET_ASSETS: 'tutorial_pet_assets',
  },

  CHEATS: {
    _: 'cheats',
  },

  FAQ: {
    _: 'faq',
    SHOW_SUPPORT_WIDGET: 'faq_show_support_widget',
  },

  TEAM: {
    _: 'team',
    MEMBER_ENTRY: 'team_member_entry',
    TOOLTIP_CTA: 'team_tooltip_cta',
    KICK_MEMBER: 'team_kick_member',
    BLOCK_TOGGLE: 'team_block_toggle',
    GET_TEAM_PROFILE: 'team_get_team_profile',
    GET_TEAM_MEMBERS: 'team_get_team_members',
    SEARCH_TEAMS: 'team_search_teams',
    RECOMMENDED_TEAMS: 'team_recommended_teams',
    CHEST_LEADERBOARD: 'team_chest_leaderboard',
    TAB_COMPETITION: 'team_tab_competition',
    TAB_MY_TEAM: 'team_tab_my_team',
    MATCHMAKING: 'team_matchmaking',
    CONPETITION_EVENT: 'team_competition_event',
    CHEST_EVENT: 'team_chest_event',
    GET_UNCLAIMED_COMPETITION: 'team_get_unclaimed_competition',
    CLAIM_EVENT_REWARDS: 'team_claim_event_rewards',
    SEND_ENERGY: 'team_send_energy',
    GET_CHAT_MESSAGES: 'team_get_chat_messages',
    CHEST_HEADER_UPDATE: 'team_chest_header_update',
    CREATE_TEAM: 'team_create_team',
    JOIN_TEAM: 'team_join_team',
  },

  IAP: {
    _: 'iap',
    READY: 'iap_ready',
    BEFORE_NATIVE_SWITCH: 'iap_before_native_switch',
    COMPLETE_STRIPE_PAYMENT: 'iap_complete_stripe_payment',
    FULFILL_UNRECORDED_PAYMENTS: 'iap_fulfill_unrecorded_payments',
    PROVISION_PRODUCT: 'iap_provision_product',
    PURCHASE_CONFIRMATION_OA: 'iap_purchase_confirmation_oa',

    POPUP_CANCEL_PURCHASE: {
      _: 'iap_popup_cancel_purchase',
      SUPPORT: 'iap_popup_cancel_purchase_support',
    },
    POPUP_PURCHASE_COMPLETE: {
      _: 'iap_popup_purchase_complete',
      OK: 'iap_popup_purchase_complete_ok',
    },
    SHOW_SUPPORT_WIDGET_ON_ERROR: 'iap_show_support_widget_on_error',
    SHOW_SUPPORT_WIDGET_ON_CANCEL: 'iap_show_support_widget_on_cancel',
  },

  SCENE_MANAGER: {
    _: 'scene_manager',
    PRELOAD: 'scene_manager_preload',
  },

  CHEST: {
    _: 'chest',
    ASSETS: 'chest_assets',
  },

  EDITOR: {
    _: 'editor',
    LEVEL_ASSETS: 'editor_level_assets',
  },

  LEAGUES: {
    _: 'leagues',
    END_EVENTS: 'leagues_end_events',
    GET_SCOREBOARD: 'leagues_get_scoreboard',
    GET_LEADERBOARD: 'leagues_get_leaderboard',
    HOME_BUTTON: 'leagues_home_button',
    ROOM_BUTTON: 'leagues_room_button',
    TROPHY: 'leagues_trophy',
    TITLE: 'leagues_title',
    LEADERBOARD_TIER_CHANGE: 'leagues_leaderboard_tier_change',
    CLAIM_REWARDS: 'leagues_claim_rewards',
    INFO_CONTINUE: 'leagues_info_continue',
  },

  MAGIC_LAMPS: {
    _: 'magic_lamps',
    ASSETS: 'magic_lamps_assets',
  },

  TOURNAMENT: {
    _: 'tournament',
    REFRESH: 'tournament_refresh',
    INVALIDATE_CACHE: 'tournament_invalidate_cache',
  },

  ERROR: {
    _: 'error',
    REFRESH_USER_STATE: 'error_refresh_user_state',
  },

  TIME_PRESSURE: {
    _: 'time_pressure',
    INVITE: 'time_pressure_invite',
  },

  BPOINTS_FROM_PUZZLE: {
    _: 'bpoints_from_puzzle',
    TUTORIAL: 'bpoints_from_puzzle_tutorial',
    TOS_ACCEPT: 'bpoints_from_puzzle_tos_accept',
    OPEN_CRATE_POPUP: 'bpoints_from_puzzle_open_crate_popup',
    OPEN_CRATE_ACCEPT: 'bpoints_from_puzzle_open_crate_accept',
  },

  PET_STARTER_PACK: {
    _: 'pet_starter_pack',
    POPUP: 'pet_starter_pack_popup',
    POPUP_OK: 'pet_starter_pack_popup_ok',
    PURCHASE: 'pet_starter_pack_purchase',
    SIDE_BUTTON: 'pet_starter_pack_side_button',
  },

  SHARE_INVITE: {
    _: 'share_invite',
    POPUP: 'share_invite_popup',
    POPUP_CTA: 'share_invite_popup_cta',
  },

  SUPER_LOLLIPOP: {
    _: 'super_lollipop',
    BUTTON: 'super_lollipop_button',
    POPUP_PROGRESS: 'super_lollipop_popup_progress',
    POPUP_PROGRESS_INFO: 'super_lollipop_popup_progress_info',
    POPUP_PROGRESS_CTA: 'super_lollipop_popup_progress_cta',
    POPUP_INFO: 'super_lollipop_popup_info',
    POPUP_ACTIVE_CTA: 'super_lollipop_popup_cta',
    POPUP_LOST: 'super_lollipop_popup_lost',
  },

  DREAM_DRIFT: {
    _: 'dream_drift',
    BUTTON: 'dream_drift_button',
    BUTTON_INFO: 'dream_drift_button_info',
    POPUP_INFO: 'dream_drift_popup_info',
    POPUP_CHEST: 'dream_drift_popup_chest',
  },

  WEEKLY_LEADERBOARD: {
    _: 'weekly_leaderboard',
    BUTTON: 'weekly_leaderboard_button',
    POPUP_INFO: 'weekly_leaderboard_popup_info',
    TAB_WEEKLY: 'weekly_leaderboard_tab_weekly',
    TAB_FRIENDS: 'weekly_leaderboard_tab_friends',
    TAB_PLAYERS: 'weekly_leaderboard_tab_players',
    CLAIM: 'weekly_leaderboard_claim',
    TRY_AGAIN: 'weekly_leaderboard_try_again',
    POPUP_LEADERBOARD: 'weekly_leaderboard_popup_leaderboard',
    GET_FRIENDS: 'weekly_leaderboard_get_friends',
    GET_TEAMS: 'weekly_leaderboard_get_teams',
    TOP_BUTTON: 'weekly_leaderboard_top_button',
    BOTTOM_BUTTON: 'weekly_leaderboard_bottom_button',
    CLAIM_REWARDS: 'weekly_leaderboard_claim_rewards',
    LOADING_RANKING: 'weekly_leaderboard_loading_ranking',
    MATCHMAKING: 'weekly_leaderboard_matchmaking',
    POPUP_CHEST: 'weekly_leaderboard_popup_chest',
    PLAYER_ENTRY: 'weekly_leaderboard_player_entry',
    TEAM_ENTRY: 'weekly_leaderboard_team_entry',
  },

  TREASURE_EXPRESS: {
    _: 'treasure_express',
    BUTTON: 'treasure_express_button',
    CHEST_REWARDS: 'treasure_express_chest_rewards',
  },

  JANKEN: {
    _: 'janken',
    BUTTON: 'janken_button',
    WIN_REWARD: 'janken_win_reward',
    LUCKY_ITEM: 'janken_luckyItem',
    LINE_CHALLENGE: 'janken_line_share_challenge',
    LINE_REMATCH: 'janken_line_share_rematch',
    NATIVE_CHALLENGE: 'janken_native_share_challenge',
    NATIVE_REMATCH: 'janken_native_share_rematch',
    /** @deprecated Old subFeature kept so in-flight legacy janken links keep resolving. New senders use `LINE_CHALLENGE`. */
    LEGACY_LINE_CHALLENGE: 'janken_line_challenge',
    /** @deprecated Old subFeature kept so in-flight legacy janken links keep resolving. New senders use `LINE_REMATCH`. */
    LEGACY_LINE_REMATCH: 'janken_line_rematch',
    /** @deprecated Old subFeature kept so in-flight legacy janken links keep resolving. New senders use `NATIVE_CHALLENGE`. */
    LEGACY_NATIVE_CHALLENGE: 'janken_native_challenge',
    /** @deprecated Old subFeature kept so in-flight legacy janken links keep resolving. New senders use `NATIVE_REMATCH`. */
    LEGACY_NATIVE_REMATCH: 'janken_native_rematch',
    POPUP_MAIN: 'janken_popup_main',
    POPUP_CARE: 'janken_popup_care',
    POPUP_CTA: 'janken_popup_cta',
    INFO: 'janken_info',
    CLOSE: 'janken_close',
    BLOCKED: 'janken_blocked',
    NOT_ENOUGH_COINS: 'janken_not_enough_coins',
    EXPIRED: 'janken_expired',
    ALREADY_PLAYED: 'janken_already_played',
    REROLL: 'janken_reroll',
  },

  /**
   * @deprecated Leftover from the shelved newUI experiment (A/B 0403), whose
   * code was removed. What survives here is only what the live friend-profile
   * panel and unfriend-confirmation popup still emit — those two components
   * outlived newUI and were moved into `src/game/friends/`. Their values are
   * kept verbatim so the Amplitude history of those buttons stays continuous.
   * Do not change the string values; do not add new members.
   */
  NEWUI: {
    // Required by the FeatureAnalytics type: every feature group needs a `_`.
    _: 'newui',
    NAV_FRIENDS: {
      FRIEND_PROFILE: {
        _: 'newui_nav_friends_friend_profile',
        ADD_FRIEND: 'newui_nav_friends_friend_profile_add_friend',
        UNFRIEND: 'newui_nav_friends_friend_profile_unfriend',
      },
      UNFRIEND: {
        _: 'newui_nav_friends_unfriend',
        CONFIRMATION_POPUP: 'newui_nav_friends_unfriend_confirmation_popup',
        UNFRIEND: 'newui_nav_friends_unfriend_confirmation_popup_unfriend',
        KEEP_FRIEND:
          'newui_nav_friends_unfriend_confirmation_popup_keep_friend',
      },
    },
  },

  HOME_SHORTCUT: {
    _: 'home_shortcut',
    ADD: 'home_shortcut_add',
    LAUNCH: 'home_shortcut_launch',
    BUTTON: 'home_shortcut_button',
    POPUP: {
      _: 'home_shortcut_popup',
      CLOSE: 'home_shortcut_popup_close',
      CTA: 'home_shortcut_popup_cta',
    },
    REWARD_POPUP: {
      _: 'home_shortcut_reward_popup',
      CONTINUE: 'home_shortcut_reward_popup_continue',
    },
  },

  RESCUE_LEVEL: {
    _: 'rescue_level',
    POPUP_PLAY: 'rescue_level_popup_play',
    POPUP_WIN: 'rescue_level_popup_win',
    PLAY: 'rescue_level_play',
    SKIP: 'rescue_level_skip',
    THEME_ASSETS: 'rescue_level_theme_assets',
  },

  RESURRECTION: {
    _: 'resurrection',
    PET_REVIVAL: 'resurrection_pet_revival',
  },

  BUDDIES: {
    _: 'buddies',
    INVITE_OA: 'buddies_invite_oa',
    NUDGE_OA: 'buddies_nudge_oa',
    POPUP_INFO: 'buddies_popup_info',
    BUTTON_INFO: 'buddies_button_info',
    BUTTON_START: 'buddies_button_start',
    BUTTON_ACCEPT: 'buddies_button_accept',
    BUTTON_DECLINE: 'buddies_button_decline',
    BUTTON_COLLECT: 'buddies_button_collect',
    BUTTON_INVITE: 'buddies_button_invite',
    LINE_SHARE: 'buddies_line_share',
    NATIVE_SHARE: 'buddies_native_share',
    /** @deprecated Old subFeature kept so in-flight legacy buddies links keep resolving. New senders use `LINE_SHARE`. */
    LEGACY_LINE_SHARE: 'buddies_line',
    /** @deprecated Old subFeature kept so in-flight legacy buddies links keep resolving. New senders use `NATIVE_SHARE`. */
    LEGACY_NATIVE_SHARE: 'buddies_native',
    SHARE_INVITE: 'buddies_share_invite',
    TAB_FRIENDS: 'buddies_tab_friends',
    TAB_REQUESTS: 'buddies_tab_requests',
    LOCKED: 'buddies_locked',
  },

  PIGGY_BANK: {
    _: 'piggy_bank',
    BUTTON: 'piggy_bank_button',
    POPUP: 'piggy_bank_popup',
    POPUP_INTRO: 'piggy_bank_popup_intro',
    BREAK: 'piggy_bank_break',
    OOM: 'piggy_bank_oom',
  },
} as const;

// Get the type of the Feature object
type TypeOfFeature = typeof Feature;

// All string leaves reachable from T. The `T extends string` conditional
// distributes over union inputs, so passing a union of feature objects (e.g.
// for SubFeatureType) yields the union of every branch's leaves.
type AllStringValues<T> = T extends string
  ? T
  : T extends object
    ? { [K in keyof T]: AllStringValues<T[K]> }[keyof T]
    : never;

/**
 * Allows to set types for feature and subFeature pairs, and check for invalid combinations
 *
 * @example
 *
 * // Valid
 * const buddiesAnalytics: FeatureAnalytics = {
 *   feature: 'baking_buddies',
 *   subFeature: 'baking_buddies_feature_ui',
 * };
 *
 * // Not valid
 * const buddiesAnalytics: FeatureAnalytics = {
 *   feature: 'baking_buddies',
 *   subFeature: 'wrong_sub_feature',
 * };
 */
export type FeatureAnalytics = {
  [K in keyof TypeOfFeature]: {
    feature: TypeOfFeature[K]['_'];
    subFeature: AllStringValues<TypeOfFeature[K]>;
  };
}[keyof TypeOfFeature];

// Features types that splitted into feature and subFeature
export type FeatureType = TypeOfFeature[keyof TypeOfFeature]['_'];
export type SubFeatureType = AllStringValues<
  TypeOfFeature[keyof TypeOfFeature]
>;
export type AnalyticsEvent = ReplicantAnalyticsEvent<GameAnalyticsSchema>;

/**
 * Type safe function for sending analytics events from the replicant code
 *
 * @example
 * // 1. Create module schema:
 * import { A } from 'src/lib/analytics/utils';
 *
 * export const events = {
 *  ItemSell: A.event('Sent when the player finished a game of Match 3', {
 *    itemTierID: A.string('The tier ID of the item'),
 *    ...
 *  }),
 * };
 *
 * // 2. Import the schema to analytics.config.ts:
 * import { events as catalog, properties as catalogProperties } from 'src/replicant/catalog/catalog.analytics';
 *
 * export default A.createSchema('Buruburu', {
 *  events: {
 *    ...,
 *    ...catalog,
 *  },
 *  userProperties: {
 *  ...,
 *  ...catalogProperties,
 *  }
 * });
 *
 * // 2. Create a function that send the event:
 * sendAnalyticsEvents(api, [
 *  {
 *    eventType: 'ItemSell',
 *    eventProperties: {
 *      itemTierID: item.tierId,
 *      ...
 *    },
 *  },
 * ]);
 */
export const sendAnalyticsEvents: SendAnalyticsEventsFunction<
  GameAnalyticsSchema
> = (api, events) => {
  // eslint-disable-next-line no-restricted-syntax
  api.sendAnalyticsEvents(events);
};
